import { Column, Index } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

export type ReportOutputFileStatusModel = 'queued' | 'running' | 'ready' | 'failed' | 'expired';

/**
 * PD-17460 — one report file (PDF, Excel or CSV) built by the background
 * worker: a person's download from a report page, or an email attachment too
 * large to attach. The request is stored so the worker builds exactly what
 * was asked, as the person who asked. The file itself sits in the private S3
 * bucket for 7 days.
 */
@Index('idx_report_output_file_user', ['fkUserIdRequested', 'createdDate'])
@Index('idx_report_output_file_expires', ['expiresDate'])
@Index('uq_report_output_file_token', ['publicToken'], { unique: true })
// auditable: false - file bookkeeping written by the background worker; the request is the person's own.
@Entity('report_output_file', {}, { auditable: false })
export class ReportOutputFileEntity extends BaseEntity {
	/** Tenant `user._id` who asked for it; null for an email attachment. */
	@Column('int', { name: 'fk_user__id_requested', unsigned: true, nullable: true })
	public fkUserIdRequested!: number | null;

	/** The Workd user id the file is built as (whose access shapes the numbers). */
	@Column('int', { name: 'workdUserIdBuiltAs', unsigned: true })
	public workdUserIdBuiltAs!: number;

	@Column('varchar', { name: 'format', length: 10 })
	public format!: string;

	/** The ReportOutputRequestDto, as JSON. */
	@Column('mediumtext', { name: 'request' })
	public request!: string;

	@Column('varchar', { name: 'status', length: 20, default: 'queued' })
	public status!: ReportOutputFileStatusModel;

	@Column('varchar', { name: 'fileName', length: 255, nullable: true })
	public fileName!: string | null;

	@Column('varchar', { name: 'contentType', length: 120, nullable: true })
	public contentType!: string | null;

	@Column('int', { name: 'sizeBytes', unsigned: true, nullable: true })
	public sizeBytes!: number | null;

	/** S3 key in the private bucket, once built. */
	@Column('varchar', { name: 's3Key', length: 500, nullable: true })
	public s3Key!: string | null;

	/** Why it failed, in words. */
	@Column('varchar', { name: 'error', length: 500, nullable: true })
	public error!: string | null;

	/**
	 * When the person's Downloads panel last asked about this file. If it is
	 * still watching when the file is ready, the panel downloads it; if not
	 * (the tab was closed), a bell notification brings the link instead.
	 */
	@Column('datetime', { name: 'lastWatchedDate', nullable: true })
	public lastWatchedDate!: Date | null;

	@Column('datetime', { name: 'readyDate', nullable: true })
	public readyDate!: Date | null;

	/** When the browser was first handed the file; the bell is only for files nobody downloaded. */
	@Column('datetime', { name: 'downloadedDate', nullable: true })
	public downloadedDate!: Date | null;

	/** After this the file is deleted and the link stops working. */
	@Column('datetime', { name: 'expiresDate' })
	public expiresDate!: Date;

	/**
	 * Unguessable token for a link in an email, for recipients who may not
	 * sign in (outside addresses). Null for a person's own download.
	 */
	@Column('varchar', { name: 'publicToken', length: 64, nullable: true })
	public publicToken!: string | null;
}
